import { convexClient, crossDomainClient } from '@convex-dev/better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react'

import { PUBLIC_CONVEX_SITE_URL } from '@/lib/env'

// The SPA is served from a different origin than the Convex HTTP routes that
// host Better Auth. Point the client at the Convex site URL and use the
// crossDomain plugin (paired with `crossDomain()` on the server in convex/auth.ts)
// so the session token is carried via Authorization header / localStorage
// instead of cookies.
// Inside a cross-site iframe (embedded donation forms, campaign cards, free
// tools) Chrome with third-party cookies blocked, Safari, and Brave throw a
// SecurityError on *any* localStorage access. The cross-domain plugin reads
// storage while the app boots, so without this guard the whole embed rendered
// blank for those visitors. Fall back to in-memory storage: donors never sign
// in inside an embed, so nothing needs to persist there.
function createAuthStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const storage = window.localStorage
    storage.getItem('kindlumen:storage-probe')
    return storage
  } catch {
    const memory = new Map<string, string>()
    return {
      get length() {
        return memory.size
      },
      clear: () => memory.clear(),
      getItem: (key: string) => memory.get(key) ?? null,
      key: (index: number) => [...memory.keys()][index] ?? null,
      removeItem: (key: string) => {
        memory.delete(key)
      },
      setItem: (key: string, value: string) => {
        memory.set(key, String(value))
      },
    }
  }
}

export const authClient = createAuthClient({
  baseURL: PUBLIC_CONVEX_SITE_URL,
  plugins: [convexClient(), crossDomainClient({ storage: createAuthStorage() })],
})
